/* Motorika — theme behaviour. No dependencies. */
(() => {
  const theme = window.theme || {};
  const routes = window.routes || {};
  const strings = theme.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Header: transparent over the hero until scrolled ---------- */
  const header = $('[data-header]');
  if (header && header.dataset.transparent === 'true') {
    const update = () => {
      const open = document.body.classList.contains('drawer-open');
      header.classList.toggle('is-transparent', window.scrollY < 24 && !open);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener('drawer:change', update);
  }

  /* ---------- Announcement bar ---------- */
  const bar = $('[data-announcement]');
  if (bar) {
    const msgs = $$('[data-announcement-msg]', bar);
    let i = 0;
    let paused = false;
    if (msgs.length > 1 && !reduceMotion) {
      bar.addEventListener('mouseenter', () => (paused = true));
      bar.addEventListener('mouseleave', () => (paused = false));
      bar.addEventListener('focusin', () => (paused = true));
      bar.addEventListener('focusout', () => (paused = false));
      setInterval(() => {
        if (paused) return;
        msgs[i].hidden = true;
        i = (i + 1) % msgs.length;
        msgs[i].hidden = false;
      }, 5000);
    }
    $('[data-announcement-close]', bar)?.addEventListener('click', () => {
      bar.hidden = true;
      try { sessionStorage.setItem('motorika-announcement', 'closed'); } catch (e) {}
    });
    try { if (sessionStorage.getItem('motorika-announcement') === 'closed') bar.hidden = true; } catch (e) {}
  }

  /* ---------- Drawers (cart + mobile menu) ---------- */
  let lastTrigger = null;
  const focusables = (el) =>
    $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter((n) => n.offsetParent !== null);

  function openDrawer(drawer, trigger) {
    if (!drawer) return;
    lastTrigger = trigger || document.activeElement;
    drawer.classList.remove('is-closing');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    document.body.style.overflow = 'hidden';
    document.dispatchEvent(new CustomEvent('drawer:change'));
    const panel = $('.drawer__panel', drawer);
    requestAnimationFrame(() => (focusables(panel)[0] || panel).focus());
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.add('is-closing');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    document.body.style.overflow = '';
    document.dispatchEvent(new CustomEvent('drawer:change'));
    lastTrigger?.focus?.();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) closeDrawer(closer.closest('.drawer'));
  });

  document.addEventListener('keydown', (e) => {
    const drawer = $('.drawer.is-open');
    if (!drawer) return;
    if (e.key === 'Escape') return closeDrawer(drawer);
    if (e.key !== 'Tab') return;
    const items = focusables($('.drawer__panel', drawer));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- Slideshows (hero + reviews) ---------- */
  $$('[data-slider]').forEach((root) => {
    const slides = $$('[data-slide]', root);
    if (slides.length < 2) return;
    const counter = $('[data-slide-counter]', root);
    const live = $('[data-slide-live]', root);
    let index = slides.findIndex((s) => s.classList.contains('is-active'));
    if (index < 0) index = 0;
    let timer = null;
    let userPaused = false;

    const go = (n, announce = true) => {
      slides[index].classList.remove('is-active');
      slides[index].setAttribute('aria-hidden', 'true');
      slides[index].inert = true;
      index = (n + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      slides[index].setAttribute('aria-hidden', 'false');
      slides[index].inert = false;
      const label = `${index + 1} / ${slides.length}`;
      if (counter) counter.textContent = label;
      if (live && announce) live.textContent = (strings.slide || 'Slide') + ' ' + label;
    };
    slides.forEach((s, i) => { if (i !== index) { s.setAttribute('aria-hidden', 'true'); s.inert = true; } });

    $('[data-slide-prev]', root)?.addEventListener('click', () => { go(index - 1); restart(); });
    $('[data-slide-next]', root)?.addEventListener('click', () => { go(index + 1); restart(); });

    const pauseBtn = $('[data-slide-pause]', root);
    const interval = parseInt(root.dataset.autoplay || '0', 10) * 1000;
    const canAuto = interval > 0 && !reduceMotion;
    if (pauseBtn) pauseBtn.hidden = !canAuto;

    function start() { if (canAuto && !userPaused && !timer) timer = setInterval(() => go(index + 1, false), interval); }
    function stop() { clearInterval(timer); timer = null; }
    function restart() { stop(); start(); }

    if (canAuto) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      pauseBtn?.addEventListener('click', () => {
        userPaused = !userPaused;
        pauseBtn.setAttribute('aria-pressed', String(userPaused));
        pauseBtn.setAttribute('aria-label', userPaused ? strings.play || 'Play' : strings.pause || 'Pause');
        $('[data-icon-pause]', pauseBtn).hidden = userPaused;
        $('[data-icon-play]', pauseBtn).hidden = !userPaused;
        userPaused ? stop() : start();
      });
      start();
    }

    root.addEventListener('keydown', (e) => {
      if (e.target.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') { go(index - 1); restart(); }
      if (e.key === 'ArrowRight') { go(index + 1); restart(); }
    });
  });

  /* ---------- Cart (Shopify AJAX API, with an adapter hook for the static preview) ---------- */
  const SECTION_ID = 'cart-drawer';
  const ShopifyCart = {
    async add(formData) {
      formData.append('sections', SECTION_ID);
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: [SECTION_ID], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      return data;
    },
  };
  const cart = window.MotorikaCartAdapter || ShopifyCart;
  const cartDrawer = document.getElementById('CartDrawer');

  function renderCart(sections) {
    const html = sections && sections[SECTION_ID];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.getElementById('CartDrawerContents');
    const current = document.getElementById('CartDrawerContents');
    if (next && current) current.replaceWith(next);
    const count = next?.dataset.countValue ?? '0';
    $$('[data-cart-count]').forEach((el) => {
      el.dataset.count = count;
      el.textContent = count === '0' ? '' : count;
    });
    const label = $('[data-cart-label]');
    if (label && strings.cartLabel) label.setAttribute('aria-label', strings.cartLabel.replace('__COUNT__', count));
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form || !cartDrawer) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) errorEl.hidden = true;
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    try {
      const data = await cart.add(new FormData(form));
      renderCart(data.sections);
      openDrawer(cartDrawer, btn);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
    } finally {
      btn?.classList.remove('is-loading');
      btn?.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('click', async (e) => {
    const ctl = e.target.closest('[data-line-change]');
    if (!ctl) return;
    e.preventDefault();
    const line = parseInt(ctl.dataset.line, 10);
    const qty = parseInt(ctl.dataset.lineChange, 10);
    const wrap = document.getElementById('CartDrawerContents');
    wrap?.setAttribute('aria-busy', 'true');
    try {
      const data = await cart.change(line, Math.max(0, qty));
      renderCart(data.sections);
      $('#CartDrawer .drawer__panel')?.focus();
    } catch (err) {
      const errEl = $('[data-cart-error]');
      if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
    } finally {
      document.getElementById('CartDrawerContents')?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Quantity steppers on forms ---------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.closest('.qty')?.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(step.dataset.qtyStep, 10));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product page: media + variant picker ---------- */
  $$('[data-product]').forEach((root) => {
    const main = $('[data-media-main]', root);
    $$('[data-media-thumb]', root).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const img = $('img', main);
        if (img) { img.src = thumb.dataset.src; img.srcset = thumb.dataset.srcset || ''; img.alt = thumb.dataset.alt || ''; }
        $$('[data-media-thumb]', root).forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
      });
    });

    const jsonEl = $('[data-product-json]', root);
    if (!jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const prices = JSON.parse($('[data-variant-prices]', root)?.textContent || '{}');
    const idInput = $('input[name="id"]', root);
    const priceEl = $('[data-price]', root);
    const addBtn = $('[data-add-button]', root);
    const groups = $$('[data-option-group]', root);

    root.addEventListener('change', (e) => {
      if (!e.target.closest('[data-option-group]')) return;
      const selected = groups.map((g) => $('input:checked', g)?.value);
      const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      if (!variant) {
        addBtn.disabled = true;
        $('span', addBtn).textContent = strings.unavailable || 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      if (priceEl && prices[variant.id]) priceEl.innerHTML = prices[variant.id];
      addBtn.disabled = !variant.available;
      $('span', addBtn).textContent = variant.available ? strings.addToCart : strings.soldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media && main) {
        const thumb = $(`[data-media-thumb][data-media-id="${variant.featured_media.id}"]`, root);
        thumb?.click();
      }
    });
  });

  /* ---------- Product recommendations (loaded after the page) ---------- */
  $$('[data-recommendations]').forEach(async (el) => {
    if (el.children.length) return;
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
      if (next && next.children.length) el.innerHTML = next.innerHTML;
      else el.hidden = true;
    } catch (e) {
      el.hidden = true;
    }
  });

  /* ---------- Collection sorting ---------- */
  $$('[data-sort-select]').forEach((sel) => sel.addEventListener('change', () => sel.form.submit()));
})();
